/**
 * MCPTT Manager protocol types.
 *
 * Mirrors the constants/messages documented in docs/protocol.md and
 * extracted from bundle-source/FleetConsole/api/ServerApi.js.
 */

// ============================================================
// Enums (numeric codes used in the wire protocol)
// ============================================================

/** DATAEX.DataType — what kind of object the DataObjects array contains.
 *  Values added/confirmed via wire capture
 *  (docs/protocol-captures/01-login-roster-emergency.har.json). */
export enum ObjectType {
  Devices = 10,
  Users = 11,
  Groups = 12,
  Statuses = 13,
  LocationRequests = 15,
  GroupPatches = 18,
  QueryAvatar = 20,
  AvatarResponse = 21,
  /** User/group custom-field templates. Type 3=user, Type 4=group. */
  ExtendedFieldsTemplate = 24,
  /** Per-device media settings. Sparse. */
  Media = 25,
  EmergencyProfiles = 30,
  /** SIP/PSTN contacts: `{ ID, Name, URI }`. */
  SipContacts = 33,
  /** Push-only device parameter delta (battery, selected group).
   *  Top-level fields carry the delta; DataObjects is null. */
  DeviceParameters = 40,
  Networks = 100,
  /** Admin-only roster of web operators (with plaintext passwords).
   *  Treat with care — never log this frame. */
  Dispatchers = 105,
  NetworkSettings = 110,
}

/** DATAEX.Operation — what to do with the objects */
export enum DataOp {
  Initialize = 0,
  Add = 1,
  Remove = 2,
  Change = 3,
}

/** PTT_REQUEST.Type — what kind of call to start/stop */
export enum PttRequestType {
  PrivateVoiceStart = 0,
  PrivateVoiceStop = 1,
  GroupVoiceStart = 2,
  GroupVoiceStop = 3,
  PrivateVideoStart = 4,
  PrivateVideoStop = 5,
  GroupVideoStart = 6,
  GroupVideoStop = 7,
}

/** PTT_CONTROL.Control — server-pushed call lifecycle event */
export enum PttControlType {
  VoicePrivateBegin = 0,
  VoicePrivateEnter = 9,
  VoicePrivatePressed = 1,
  VoicePrivateReleased = 2,
  VoicePrivateEnd = 3,
  VoiceGroupBegin = 4,
  VoiceGroupEnter = 10,
  VoiceGroupPressed = 5,
  VoiceGroupReleased = 6,
  VoiceGroupEnd = 7,
  VideoPrivateBegin = 11,
  VideoPrivateEnter = 20,
  VideoPrivatePressed = 12,
  VideoPrivateReleased = 13,
  VideoPrivateEnd = 14,
  VideoGroupBegin = 15,
  VideoGroupEnter = 21,
  VideoGroupPressed = 16,
  VideoGroupReleased = 17,
  VideoGroupEnd = 18,
}

/** STORAGE_JOB_*.State — text-message / file-transfer state machine */
export enum JobState {
  Fail = -10,
  None = 0,
  Sending = 10,
  Sent = 20,
  Confirming = 30,
  Lost = 40,
  Confirmed = 50,
  Delivered = 60,
  DeliveredConfirmed = 70,
  Cancelled = 80,
  CancelledConfirmed = 90,
}

/** LOGIN_RESPONSE.Response — auth result code */
export enum LoginResponseCode {
  Success = 0,
  InvalidVersion = 1,
  InvalidCredentials = 2,
  LicenseExpired = 3,
  TooManyConnections = 4,
  DemoModeMustRestart = 5,
  MultipleLoginsProhibited = 6,
}

/** Message-target classification (used in text/file send requests) */
export enum TargetType {
  User = 0,
  Group = 1,
  Device = 2,
}

/** Bitfield permissions on a User object */
export enum Permission {
  VoiceCall = 1,                // 2 ^ 0
  VoiceCallPtt = 2,             // 2 ^ 1
  VideoCall = 8,                // 2 ^ 3
  VideoCallPtt = 16,            // 2 ^ 4
  TextMessage = 64,             // 2 ^ 6
  LocationTracking = 128,       // 2 ^ 7
  CallAlert = 256,              // 2 ^ 8
  ShowEventLog = 131072,        // 2 ^ 17
  ShowSearch = 262144,          // 2 ^ 18
  ShowSettings = 524288,        // 2 ^ 19
  RestrictPasswordChange = 1048576,    // 2 ^ 20
  ManageUsersGroups = 2097152,         // 2 ^ 21
  Monitoring = 4194304,                // 2 ^ 22
  CallRecordingReport = 8388608,       // 2 ^ 23
  ManageDeviceSettings = 16777216,     // 2 ^ 24
  RestrictLogoff = 33554432,           // 2 ^ 25
  RestrictQuit = 67108864,             // 2 ^ 26
  AllowEmergencyReport = 134217728,    // 2 ^ 27
  GuardTour = 268435456,               // 2 ^ 28
  GroupPatches = 536870912,            // 2 ^ 29
  AllowUserInventoryReport = 1073741824, // 2 ^ 30
  // 64-bit values — JS bit ops are 32-bit, use BigInt for these:
  RestrictPeriodicLocations = 4294967296,         // 2 ^ 32
  RestrictLocationTrackReport = 8589934592,       // 2 ^ 33
  DynamicGroups = 17179869184,                     // 2 ^ 34
  ManageAllDynamicGroups = 34359738368,            // 2 ^ 35
  AllowPhoneCalls = 68719476736,                   // 2 ^ 36
  RestrictRecordPlayback = 137438953472,           // 2 ^ 37
  Beacons = 274877906944,                          // 2 ^ 38
}

// ============================================================
// Domain objects
// ============================================================

/** Base64-encoded GUID (16 bytes -> 24 chars) — manager's universal ID format */
export type Base64Guid = string;

export interface Network {
  ID: Base64Guid;
  Name: string;
  /** populated by the client as DATAEX messages stream in */
  users?: User[];
  groups?: Group[];
  devices?: Device[];
  statuses?: Status[];
  emergency?: EmergencyProfile[];
  patches?: GroupPatch[];
  /** raw fields the manager may send — keep open */
  [key: string]: unknown;
}

export interface User {
  ID: Base64Guid;
  Login: string;
  Name?: string;
  Email?: string;
  /** 0 = device user (radio operator), 1 = dispatcher (web client) */
  Type?: 0 | 1;
  Permissions?: number;        // lower 32 bits
  Permissions2?: number;       // upper bits (e.g. value 12 seen for full SysAdmin)
  AvatarHash?: string;
  StatusID?: Base64Guid;
  Locked?: boolean;
  [key: string]: unknown;
}

/** Push-only payload from DataType=40 frames. Apply onto Device.Parameters. */
export interface DeviceParameters {
  BATTERY_CHARGING?: boolean;
  BATTERY_LEVEL?: number;       // 0..100
  SelectedGroupID?: Base64Guid;
  SelectedGroupName?: string;
  [key: string]: unknown;
}

export interface Device {
  ID: Base64Guid;
  UserID: Base64Guid;
  /** 0 = mobile/handheld radio, 1 = web/dispatcher client, 2 = SIP/RoIP gateway. */
  Type?: 0 | 1 | 2;
  Login?: string;
  UserName?: string;
  DeviceDescription?: string;
  AvatarHash?: string;
  VersionName?: string;
  Description?: string;
  Latitude?: number;
  Longitude?: number;
  Accuracy?: number;
  Altitude?: number;
  Speed?: number;
  Time?: number;
  GpsExpire?: number;
  StatusID?: Base64Guid;
  Parameters?: DeviceParameters;
  [key: string]: unknown;
}

export interface Group {
  ID: Base64Guid;
  Name: string;
  /** 0 = standard talkgroup. Other values reserved. */
  Type?: number;
  /** -1 (low) .. 5 (highest). 0 = normal. */
  Priority?: number;
  /** 1 = this group is the network's emergency channel. */
  Emergency?: 0 | 1;
  /** 1 = group is an "all call" — broadcast to everyone in network. */
  AllCall?: 0 | 1;
  /** 1 = broadcast group. */
  Broadcast?: 0 | 1;
  /** 1 = echo-only test group. */
  Echo?: 0 | 1;
  [key: string]: unknown;
}

export interface Status {
  ID: Base64Guid;
  Name: string;
  /** Signed 32-bit ARGB integer. Negative values are normal — convert with
   *  `(color >>> 0).toString(16)` for an unsigned hex view. */
  Color?: number;
  [key: string]: unknown;
}

export interface EmergencyProfile {
  ID: Base64Guid;
  Name: string;
  [key: string]: unknown;
}

export interface GroupPatch {
  ID: Base64Guid;
  Name: string;
  Groups: Base64Guid[];
  [key: string]: unknown;
}

/** A live or recently-finished call. Field names mirror the wire's
 *  PTT_CONTROL frame (TargetID, not DestinationID). */
export interface Call {
  CallID: Base64Guid;
  NetworkID: Base64Guid;
  /** Device that's transmitting. */
  SourceID: Base64Guid;
  /** User behind that device. */
  SourceUserID?: Base64Guid;
  SourceName?: string;
  /** Group or device being talked to. */
  TargetID: Base64Guid;
  TargetName?: string;
  /** Inferred from PTT_CONTROL.Control values (PTT_CONTROL doesn't carry a
   *  Type field; we deduce voice vs video, private vs group). */
  kind: "voice-private" | "voice-group" | "video-private" | "video-group";
  /** lifecycle state machine */
  state:
    | "initiated"   // _BEGIN / _ENTER seen
    | "speaking"    // _PRESSED seen
    | "hangtime"    // _RELEASED seen, awaiting next press or _END
    | "ended";      // _END seen
  startedAt: number;
  /** if true, ignore incoming VOICE_PACKETs for this call */
  SoundIsMuted?: boolean;
}

// ============================================================
// Wire messages (incoming / outgoing)
// ============================================================

export interface ServerConfigMessage {
  MessageID: "SERVER_CONFIG";
  VoipPort: number;
  AudioSampleRate: number;
  AudioBitRate: number;
  AudioFrameSize: number;
}

export interface DeviceConfigMessage {
  MessageID: "DEVICE_CONFIG";
  Ssrc: number;
  AppName: string;
  VersionName: string;
  VersionCode: number;
  AudioCodec: number;
  /** Note: STRING "True"/"False" here, not boolean. Manager quirk. */
  VoiceOverTcp: "True" | "False";
  Password: string;
  DeviceData: {
    SessionID: Base64Guid;
    ID: Base64Guid;
    DeviceDescription: string;
    Login: string;
    AvatarHash: string;
    /** Boolean here, not string. Manager quirk. */
    VoiceOverTcp: boolean;
    StatusID: Base64Guid;
  };
}

export interface ConfigAckMessage  { MessageID: "CONFIG_SERVER_RESPONSE_ACK" }
export interface ConfigNackMessage { MessageID: "CONFIG_SERVER_RESPONSE_NACK"; Reason: string }
export interface LoginMessage      { MessageID: "LOGIN" }
export interface LoginResponseMessage {
  MessageID: "LOGIN_RESPONSE";
  Response: LoginResponseCode;
  UserID?: Base64Guid;
}

export interface DataExMessage<T = unknown> {
  MessageID: "DATAEX";
  DataType: ObjectType;
  Operation: DataOp;
  NetworkID?: Base64Guid;
  DataObjects: T[];
}

export interface BeginInitializeMessage { MessageID: "BEGIN_INITIALIZE" }
export interface EndInitializeMessage   { MessageID: "END_INITIALIZE" }

export interface PttRequestMessage {
  MessageID: "PTT_REQUEST";
  NetworkID: Base64Guid;
  Destination: Base64Guid;
  Type: PttRequestType;
}

export interface PttControlMessage {
  MessageID: "PTT_CONTROL";
  NetworkID: Base64Guid;
  /** Device that's transmitting. */
  SourceID: Base64Guid;
  /** User behind that device (added to the wire in 5.x). */
  SourceUserID?: Base64Guid;
  SourceName?: string;
  /** Group or device being talked to. NOTE: wire uses `TargetID`, not
   *  `DestinationID`. */
  TargetID: Base64Guid;
  TargetName?: string;
  CallID: Base64Guid;
  Control: PttControlType;
  /** Not present on PTT_CONTROL — only on PTT_REQUEST. Kept optional
   *  for forward-compat. */
  Type?: PttRequestType;
}

export interface VoicePacketMessage {
  MessageID: "VOICE_PACKET";
  NetworkID: Base64Guid;
  Data: string;                // base64 of UDP-formatted RTP frame
}

export interface GpsRequestMessage {
  MessageID: "GPS_REQUEST";
  NetworkID: Base64Guid;
  DeviceID: Base64Guid;
  GroupID?: Base64Guid;
  Period: number;
  RequestID: Base64Guid;
}

export interface GpsResultMessage {
  MessageID: "GPS_RESULT_TO_CLIENT";
  NetworkID: Base64Guid;
  DeviceID: Base64Guid;
  Latitude: number;
  Longitude: number;
  Accuracy: number;
  Altitude?: number;
  Speed?: number;
  Time: number;
  Flags: number;
  FromName?: string;
}

/** SOS event pushed by the manager. Field names confirmed via capture
 *  (the earlier guess of SourceID/State/Time was wrong). */
export interface EmergencyMessage {
  MessageID: "EMERGENCY";
  EmergencyID: Base64Guid;
  ProfileID: Base64Guid;
  /** Device that triggered the emergency. */
  DeviceID: Base64Guid;
  /** User behind the device. */
  UserID: Base64Guid;
  /** Display name (typically the radio's friendly name). */
  UserName: string;
  /** 0 = manual SOS button. Other values TBC (man-down, screen-SOS, panic). */
  Reason: number;
  NetworkID: Base64Guid;
}

/** Pushed when ANY dispatcher acks an active emergency (including us). */
export interface EmergencyAckMessage {
  MessageID: "EMERGENCY_ACK";
  EmergencyID: Base64Guid;
  /** The dispatcher's device session id (who ack'd). */
  DeviceID: Base64Guid;
  /** The dispatcher user id. */
  UserID: Base64Guid;
  UserName: string;
  NetworkID: Base64Guid;
}

/** Server-pushed delta when a device's selected group or battery changes.
 *  Unique among DATAEX frames in that the payload is on the message itself,
 *  not in DataObjects. */
export interface DeviceParametersPushMessage {
  MessageID: "DATAEX";
  DataType: ObjectType.DeviceParameters;
  DeviceID: Base64Guid;
  NetworkID: Base64Guid;
  DataObjects: null;
  BATTERY_CHARGING?: boolean;
  BATTERY_LEVEL?: number;
  SelectedGroupID?: Base64Guid;
  SelectedGroupName?: string;
}

/** Per-session knobs handed to the client right after LOGIN_RESPONSE. */
export interface DeviceContextMessage {
  MessageID: "DEVICE_CONTEXT";
  /** Max ms allowed between PINGs. Client should send PING every PingTimeout/2. */
  PingTimeout: number;
  JitterSize?: number;
  VideoResolution?: number;
  BatteryChargeInterval?: number;
  /** Failover manager address (host:port). Used if primary drops. */
  SecondaryNetworkAddress?: string;
  /** Per-account web locale. */
  WebLocale?: string;
}

export interface PingMessage { MessageID: "PING" }
export interface PongMessage { MessageID: "PONG" }

/** Discriminated union of all known incoming messages */
export type IncomingMessage =
  | ServerConfigMessage
  | ConfigAckMessage
  | ConfigNackMessage
  | LoginResponseMessage
  | DeviceContextMessage
  | BeginInitializeMessage
  | EndInitializeMessage
  | DataExMessage
  | DeviceParametersPushMessage
  | PttControlMessage
  | VoicePacketMessage
  | GpsResultMessage
  | EmergencyMessage
  | EmergencyAckMessage
  | PingMessage
  | PongMessage
  | { MessageID: string; [key: string]: unknown };  // unknown / future

// ============================================================
// Audio adapter — pluggable seam for capture/playback.
//
// Phase 1 ships a no-op default. Phase 2 will provide a Web Audio
// implementation that decodes µ-law/a-law frames for playback and
// captures the mic into RTP-formatted UDP frames for outgoing PTT.
// ============================================================

export interface AudioAdapter {
  /** Begin capture. The supplied `send` will be called once per audio
   *  frame with a fully RTP-framed Uint8Array (12-byte header + samples). */
  start(
    send: (rtpFrame: Uint8Array) => void,
    opts: {
      sampleRate: number;     // from SERVER_CONFIG (e.g. 8000)
      frameSizeMs: number;    // from SERVER_CONFIG (e.g. 20)
      codec: number;          // 1 = µ-law/a-law (alawmulaw)
      ssrc: number;           // RTP SSRC, random per session
    },
  ): Promise<void>;

  /** Stop capture. */
  stop(): void;

  /** Play a single RTP frame received from the manager.
   *  The full UDP-formatted packet is passed; the adapter is responsible
   *  for stripping the 12-byte header and decoding samples. */
  playFrame(rtpFrame: Uint8Array, callId: Base64Guid): void;
}

// ============================================================
// Client-facing event payloads (what consumers subscribe to)
// ============================================================

export type ConnectionState =
  | "disconnected"
  | "connecting"
  | "configuring"  // SERVER_CONFIG -> DEVICE_CONFIG handshake
  | "logging-in"   // LOGIN sent, awaiting LOGIN_RESPONSE
  | "initializing" // BEGIN_INITIALIZE -> END_INITIALIZE roster sync
  | "ready"
  | "reconnecting"
  | "error";

/**
 * Map of event-name -> listener signature. Used to type the
 * `MCPTTClient.on(event, cb)` overloads.
 */
export interface MCPTTClientEvents {
  /** connection state changed */
  "state": (state: ConnectionState) => void;
  /** initial roster sync complete; networks fully populated */
  "ready": () => void;
  /** authentication failed (bad password, expired license, etc.) */
  "auth-error": (code: LoginResponseCode, message: string) => void;
  /** transport error (socket dropped, server unreachable) */
  "transport-error": (event: Event | string) => void;
  /** connection closed; client may auto-reconnect depending on options */
  "disconnect": (code: number, reason: string) => void;
  /** coarse snapshot — fires on any network-tree mutation */
  "networks": (networks: Network[]) => void;

  "network:add":     (network: Network) => void;
  "network:change":  (network: Network) => void;
  "network:remove":  (network: Network) => void;

  "user:add":        (user: User, networkId: Base64Guid) => void;
  "user:change":     (user: User, networkId: Base64Guid) => void;
  "user:remove":     (user: User, networkId: Base64Guid) => void;

  "group:add":       (group: Group, networkId: Base64Guid) => void;
  "group:change":    (group: Group, networkId: Base64Guid) => void;
  "group:remove":    (group: Group, networkId: Base64Guid) => void;

  "device:add":      (device: Device, networkId: Base64Guid) => void;
  "device:change":   (device: Device, networkId: Base64Guid) => void;
  "device:remove":   (device: Device, networkId: Base64Guid) => void;
  "device:gps":      (device: Device, networkId: Base64Guid) => void;

  /** any PTT call lifecycle event */
  "ptt":             (call: Call, control: PttControlType) => void;
  /** raw inbound voice packet, after call lookup but before playback.
   *  Packet is the full manager-wrapped RTP bytes:
   *  [12-byte header][16-byte CallID][A-law payload]. */
  "voice":           (call: Call, rtpFrame: Uint8Array) => void;
  /** call started receiving voice (this client should play audio) */
  "rx:start":        (call: Call) => void;
  /** call stopped receiving voice */
  "rx:stop":         (call: Call) => void;
  /** SOS event */
  "emergency":       (msg: EmergencyMessage) => void;
  /** another dispatcher (or this client) acknowledged an active SOS */
  "emergency:ack":   (msg: EmergencyAckMessage) => void;
  /** Per-session settings handed down by the manager post-login. */
  "device-context":  (msg: DeviceContextMessage) => void;
  /** raw frame fallback for messages we don't model */
  "raw":             (msg: IncomingMessage) => void;
}
